import React, { useState, useEffect, useMemo, useCallback } from 'react';
import ApiClient from '../utils/ApiClient';

const STATUS_OPTIONS = ['', 'ACTIVE', 'CONFIRMED', 'SHIPPED', 'CANCELLED', 'SUPERSEDED', 'VOIDED', 'DRAFT'];

// ---------- formatting helpers ----------
function formatMoney(v) {
    if (v == null) return '-';
    const n = typeof v === 'number' ? v : parseFloat(v);
    if (isNaN(n)) return '-';
    return `$${n.toFixed(2)}`;
}

function formatRelativeOrAbs(value) {
    if (!value) return '-';
    const d = new Date(value);
    if (isNaN(d.getTime())) return String(value);
    const diffMs = Date.now() - d.getTime();
    const ONE_DAY = 24 * 60 * 60 * 1000;
    if (diffMs < ONE_DAY && diffMs >= 0) {
        const mins = Math.floor(diffMs / 60000);
        if (mins < 1) return 'just now';
        if (mins < 60) return `${mins} min${mins === 1 ? '' : 's'} ago`;
        const hours = Math.floor(mins / 60);
        return `${hours} hour${hours === 1 ? '' : 's'} ago`;
    }
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function truncate(s, n = 14) {
    if (!s) return '';
    return s.length > n ? s.slice(0, n) + '…' : s;
}

function statusBadge(status) {
    if (!status) return <span className="px-2 py-1 rounded-full bg-gray-100 text-gray-600 text-xs">UNKNOWN</span>;
    const s = String(status).toUpperCase();
    let bg = 'bg-gray-100';
    let fg = 'text-gray-700';
    if (s === 'CONFIRMED' || s === 'SHIPPED' || s === 'CLOSED' || s === 'DELIVERED') {
        bg = 'bg-green-100'; fg = 'text-green-800';
    } else if (s === 'ACTIVE' || s === 'WORKING' || s === 'IN_TRANSIT') {
        bg = 'bg-blue-100'; fg = 'text-blue-800';
    } else if (s === 'DRAFT') {
        bg = 'bg-gray-100'; fg = 'text-gray-600';
    } else if (s === 'CANCELLED' || s === 'VOIDED' || s === 'SUPERSEDED') {
        bg = 'bg-red-100'; fg = 'text-red-800';
    } else if (s === 'READY_TO_SHIP') {
        bg = 'bg-yellow-100'; fg = 'text-yellow-800';
    }
    return <span className={`px-2 py-1 rounded-full ${bg} ${fg} text-xs font-medium whitespace-nowrap`}>{s}</span>;
}

function PlanIdCell({ planId }) {
    const [copied, setCopied] = useState(false);
    const copy = (e) => {
        e.stopPropagation();
        if (!planId) return;
        try {
            navigator.clipboard.writeText(planId);
            setCopied(true);
            setTimeout(() => setCopied(false), 1200);
        } catch (err) {
            // ignore
        }
    };
    return (
        <span className="font-mono text-xs flex items-center gap-1" title={planId}>
            <span>{truncate(planId, 14)}</span>
            <button
                onClick={copy}
                className="text-gray-400 hover:text-gray-700"
                title="Copy plan ID"
            >
                {copied ? '✓' : '📋'}
            </button>
        </span>
    );
}

function DestinationChips({ destinations }) {
    if (!destinations || destinations.length === 0) return <span className="text-gray-400">-</span>;
    const max = 3;
    const visible = destinations.slice(0, max);
    const extra = destinations.length - max;
    return (
        <div className="flex flex-wrap gap-1">
            {visible.map((d, i) => (
                <span key={`${d}-${i}`} className="px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded text-xs font-mono">
                    {d}
                </span>
            ))}
            {extra > 0 && (
                <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                    +{extra} more
                </span>
            )}
        </div>
    );
}

// ---------- detail modal ----------
function DetailModal({ planId, onClose, onAfterAction }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [cancelling, setCancelling] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await ApiClient.get(`/api/shipment-planner/queue/${planId}`);
            setData(res.data);
        } catch (err) {
            console.error(err);
            setError(err.message || 'Failed to load detail');
        }
        setLoading(false);
    }, [planId]);

    useEffect(() => { load(); }, [load]);

    const handleVoid = async () => {
        if (!window.confirm(`Cancel/void plan ${planId}? This will tell Amazon to cancel the inbound plan.`)) return;
        setCancelling(true);
        try {
            await ApiClient.post(`/api/shipment-planner/cancel/${planId}`);
            if (onAfterAction) onAfterAction();
            onClose();
        } catch (err) {
            alert('Failed to cancel: ' + (err.response?.data?.error || err.message));
        }
        setCancelling(false);
    };

    const status = data?.status;
    const canVoid = status && !['CANCELLED', 'VOIDED', 'SUPERSEDED', 'SHIPPED', 'CLOSED'].includes(String(status).toUpperCase());

    // Build per-shipment merged view from analysis_json + amazon_spapi_inboundplan_shipment.
    const mergedShipments = useMemo(() => {
        if (!data) return [];
        const placementId = data.confirmedPlacementId || data.recommendedPlacementId;
        let analysisShipments = [];
        if (data.analysis && Array.isArray(data.analysis.placementOptions)) {
            const chosen = data.analysis.placementOptions.find(p => p.placementOptionId === placementId)
                || data.analysis.placementOptions[0];
            if (chosen && Array.isArray(chosen.shipments)) {
                analysisShipments = chosen.shipments;
            }
        }
        // Index amazon shipments by destination_warehouse_id (best we have to link)
        const amzByFc = {};
        (data.shipments || []).forEach(s => {
            const k = s.destination_warehouse_id;
            if (k) amzByFc[k] = s;
        });
        if (analysisShipments.length > 0) {
            return analysisShipments.map(a => {
                const amz = amzByFc[a.destinationFc] || {};
                return {
                    destinationFc: a.destinationFc,
                    destinationCity: a.destinationCity || amz.destination_address_city,
                    destinationState: a.destinationState || amz.destination_address_state,
                    destinationPostal: amz.destination_address_postal,
                    carrierName: a.carrierName,
                    shippingMode: a.shippingMode,
                    transportCost: a.transportCost,
                    shipmentId: a.shipmentId || amz.shipment_id,
                    shipmentConfirmationId: amz.shipment_confirmation_id,
                    amazonStatus: amz.status,
                    deliveryWindowStart: amz.delivery_window_start,
                    deliveryWindowEnd: amz.delivery_window_end,
                    items: a.items || [],
                };
            });
        }
        // Fallback to amazon shipments only
        return (data.shipments || []).map(s => ({
            destinationFc: s.destination_warehouse_id,
            destinationCity: s.destination_address_city,
            destinationState: s.destination_address_state,
            destinationPostal: s.destination_address_postal,
            carrierName: null,
            shippingMode: null,
            transportCost: null,
            shipmentId: s.shipment_id,
            shipmentConfirmationId: s.shipment_confirmation_id,
            amazonStatus: s.status,
            deliveryWindowStart: s.delivery_window_start,
            deliveryWindowEnd: s.delivery_window_end,
            items: [],
        }));
    }, [data]);

    return (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 overflow-y-auto py-8">
            <div className="bg-white w-full max-w-5xl mx-4 rounded-lg shadow-xl">
                <div className="flex items-start justify-between px-6 py-4 border-b">
                    <div>
                        <div className="text-sm text-gray-500 font-mono">{planId}</div>
                        <h2 className="text-lg font-semibold text-gray-900">
                            {data?.name || 'Shipment Plan'}
                        </h2>
                        <div className="mt-1 flex items-center gap-2">
                            {statusBadge(data?.status)}
                            {data?.shippingMode && (
                                <span className="text-xs text-gray-500">Mode: {data.shippingMode}</span>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <a
                            href={`https://sellercentral.amazon.com/fba/inbound/plan/details?planId=${planId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs px-3 py-1.5 bg-indigo-600 text-white rounded hover:bg-indigo-700"
                        >
                            Open in Seller Central
                        </a>
                        {canVoid && (
                            <button
                                onClick={handleVoid}
                                disabled={cancelling}
                                className="text-xs px-3 py-1.5 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                            >
                                {cancelling ? 'Cancelling…' : 'Void plan'}
                            </button>
                        )}
                        <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-xl px-2">×</button>
                    </div>
                </div>

                <div className="px-6 py-4">
                    {loading && <div className="text-gray-500">Loading…</div>}
                    {error && <div className="text-red-600">Error: {error}</div>}

                    {data && !loading && (
                        <>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                                <div>
                                    <div className="text-xs text-gray-500">Created</div>
                                    <div className="text-sm font-medium">{formatRelativeOrAbs(data.planCreatedAt)}</div>
                                </div>
                                <div>
                                    <div className="text-xs text-gray-500">Units</div>
                                    <div className="text-sm font-medium">{data.totalUnits ?? '-'}</div>
                                </div>
                                <div>
                                    <div className="text-xs text-gray-500">Shipments</div>
                                    <div className="text-sm font-medium">{mergedShipments.length}</div>
                                </div>
                                <div>
                                    <div className="text-xs text-gray-500">Confirmed</div>
                                    <div className="text-sm font-medium">{data.confirmedAt ? formatRelativeOrAbs(data.confirmedAt) : '-'}</div>
                                </div>
                            </div>

                            <h3 className="text-sm font-semibold text-gray-900 mb-2">Shipments</h3>
                            <div className="overflow-x-auto mb-6">
                                <table className="min-w-full text-sm divide-y divide-gray-200">
                                    <thead>
                                        <tr className="bg-gray-50">
                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">FC</th>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">Destination</th>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">Carrier</th>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">Mode</th>
                                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-700">Cost</th>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">Shipment ID</th>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">Amazon Status</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {mergedShipments.length === 0 && (
                                            <tr><td colSpan={7} className="text-gray-500 px-3 py-3 text-center">No shipments</td></tr>
                                        )}
                                        {mergedShipments.map((s, i) => (
                                            <tr key={s.shipmentId || `s-${i}`}>
                                                <td className="px-3 py-2 font-mono text-xs">{s.destinationFc || '-'}</td>
                                                <td className="px-3 py-2 text-xs">
                                                    {[s.destinationCity, s.destinationState, s.destinationPostal].filter(Boolean).join(', ') || '-'}
                                                </td>
                                                <td className="px-3 py-2 text-xs">{s.carrierName || '-'}</td>
                                                <td className="px-3 py-2 text-xs">{s.shippingMode || '-'}</td>
                                                <td className="px-3 py-2 text-xs text-right">{formatMoney(s.transportCost)}</td>
                                                <td className="px-3 py-2 font-mono text-xs">
                                                    {s.shipmentConfirmationId
                                                        ? <span title={s.shipmentId}>{s.shipmentConfirmationId}</span>
                                                        : (s.shipmentId ? truncate(s.shipmentId, 14) : '-')}
                                                </td>
                                                <td className="px-3 py-2 text-xs">{s.amazonStatus ? statusBadge(s.amazonStatus) : <span className="text-gray-400">(not synced)</span>}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {data.shipments && data.shipments.length === 0 && (
                                    <div className="text-xs text-gray-500 mt-2">
                                        Note: Shipment-level Amazon sync rows have not been backfilled for this plan yet.
                                        Shipment IDs / statuses above came from the analysis snapshot.
                                    </div>
                                )}
                            </div>

                            {data.aiRecommendation && data.aiRecommendation.reason && (
                                <>
                                    <h3 className="text-sm font-semibold text-gray-900 mb-2">AI recommendation</h3>
                                    <div className="bg-gray-50 border border-gray-200 rounded p-3 text-xs text-gray-800 whitespace-pre-wrap mb-4">
                                        {data.aiRecommendation.reason}
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

// ---------- main page ----------
export default function ShipmentQueue() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [statusFilter, setStatusFilter] = useState('');
    const [search, setSearch] = useState('');
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [refreshTick, setRefreshTick] = useState(0);
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [bulkCancelling, setBulkCancelling] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                const params = new URLSearchParams();
                params.set('limit', '100');
                if (statusFilter) params.set('status', statusFilter);
                const res = await ApiClient.get(`/api/shipment-planner/queue?${params.toString()}`);
                if (!cancelled) setRows(res.data || []);
            } catch (err) {
                if (!cancelled) {
                    console.error(err);
                    setError(err.message || 'Failed to load shipment queue');
                }
            }
            if (!cancelled) setLoading(false);
        };
        fetchData();
        return () => { cancelled = true; };
    }, [statusFilter, refreshTick]);

    const filteredRows = useMemo(() => {
        if (!search) return rows;
        const q = search.toLowerCase();
        return rows.filter(r =>
            (r.inboundPlanId || '').toLowerCase().includes(q) ||
            (r.name || '').toLowerCase().includes(q)
        );
    }, [rows, search]);

    const totalCostSum = useMemo(
        () => filteredRows.reduce((acc, r) => acc + (typeof r.totalCost === 'number' ? r.totalCost : 0), 0),
        [filteredRows]
    );

    const handleRefresh = () => setRefreshTick(t => t + 1);

    // Cancellable plans visible after filters — used for bulk operations
    const cancellableRows = useMemo(() => {
        const dead = new Set(['CANCELLED', 'VOIDED', 'SUPERSEDED', 'SHIPPED', 'CLOSED']);
        return filteredRows.filter(r => !dead.has(String(r.status || '').toUpperCase()));
    }, [filteredRows]);

    const allCancellableSelected = cancellableRows.length > 0
        && cancellableRows.every(r => selectedIds.has(r.inboundPlanId));

    const toggleSelectAll = () => {
        if (allCancellableSelected) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(cancellableRows.map(r => r.inboundPlanId)));
        }
    };

    const toggleSelectOne = (planId) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(planId)) next.delete(planId);
            else next.add(planId);
            return next;
        });
    };

    const handleBulkCancel = async () => {
        const ids = Array.from(selectedIds);
        if (ids.length === 0) return;
        if (!window.confirm(`Cancel ${ids.length} plan${ids.length === 1 ? '' : 's'}? This voids them on Amazon and marks them CANCELLED in the DB.`)) {
            return;
        }
        setBulkCancelling(true);
        try {
            const res = await ApiClient.post('/api/shipment-planner/cancel-batch', { planIds: ids });
            const { succeeded = 0, failed = 0, results = [] } = res.data || {};
            const failures = results.filter(r => !r.success);
            if (failed === 0) {
                alert(`Cancelled ${succeeded} plan${succeeded === 1 ? '' : 's'}.`);
            } else {
                const sample = failures.slice(0, 3).map(f => `${f.planId.slice(0, 10)}…: ${f.error || 'unknown'}`).join('\n');
                alert(`Cancelled ${succeeded}, ${failed} failed.\n\nFirst failures:\n${sample}`);
            }
            setSelectedIds(new Set());
            handleRefresh();
        } catch (err) {
            alert('Bulk cancel failed: ' + (err.response?.data?.error || err.message));
        } finally {
            setBulkCancelling(false);
        }
    };

    return (
        <div className="px-4 sm:px-6 lg:px-8">
            <div className="sm:flex sm:items-center mb-4">
                <div className="sm:flex-auto">
                    <h1 className="text-xl font-semibold text-gray-900">Shipment Queue</h1>
                    <p className="mt-1 text-sm text-gray-600">
                        Inbound plans created via SP-API (including ones not visible in Seller Central UI lists).
                    </p>
                </div>
                <div className="mt-4 sm:mt-0 sm:ml-4 flex items-center gap-2">
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="text-sm border border-gray-300 rounded px-2 py-1"
                    >
                        {STATUS_OPTIONS.map(s => (
                            <option key={s} value={s}>{s === '' ? 'All statuses' : s}</option>
                        ))}
                    </select>
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search plan ID / name…"
                        className="text-sm border border-gray-300 rounded px-2 py-1 w-56"
                    />
                    <button
                        onClick={handleRefresh}
                        className="text-sm bg-indigo-600 text-white px-3 py-1 rounded hover:bg-indigo-700"
                    >
                        Refresh
                    </button>
                    {selectedIds.size > 0 && (
                        <button
                            onClick={handleBulkCancel}
                            disabled={bulkCancelling}
                            className="text-sm bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700 disabled:opacity-50"
                        >
                            {bulkCancelling
                                ? `Cancelling ${selectedIds.size}…`
                                : `Cancel ${selectedIds.size} selected`}
                        </button>
                    )}
                </div>
            </div>

            <div className="text-xs text-gray-500 mb-2">
                {filteredRows.length} plan{filteredRows.length === 1 ? '' : 's'} • Total cost: {formatMoney(totalCostSum)}
            </div>

            {loading && <div className="text-gray-500">Loading…</div>}
            {error && <div className="text-red-600">Error: {error}</div>}

            {!loading && !error && (
                <div className="overflow-x-auto bg-white border border-gray-200 rounded-md">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-3 py-2 w-8 text-left">
                                    <input
                                        type="checkbox"
                                        checked={allCancellableSelected}
                                        onChange={toggleSelectAll}
                                        disabled={cancellableRows.length === 0}
                                        title={cancellableRows.length === 0 ? 'No cancellable plans visible' : 'Select all cancellable'}
                                    />
                                </th>
                                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Created</th>
                                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Plan ID</th>
                                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Name</th>
                                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Status</th>
                                <th className="px-3 py-2 text-right text-xs font-semibold text-gray-700">Ship.</th>
                                <th className="px-3 py-2 text-right text-xs font-semibold text-gray-700">Units</th>
                                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">FCs</th>
                                <th className="px-3 py-2 text-right text-xs font-semibold text-gray-700">Total cost</th>
                                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Mode</th>
                                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 bg-white">
                            {filteredRows.length === 0 && (
                                <tr>
                                    <td colSpan={11} className="px-3 py-6 text-center text-sm text-gray-500">
                                        No plans found.
                                    </td>
                                </tr>
                            )}
                            {filteredRows.map(r => {
                                const status = r.status || '';
                                const upper = String(status).toUpperCase();
                                const canVoid = !['CANCELLED', 'VOIDED', 'SUPERSEDED', 'SHIPPED', 'CLOSED'].includes(upper);
                                return (
                                    <tr
                                        key={r.inboundPlanId}
                                        className="hover:bg-gray-50 cursor-pointer"
                                        onClick={() => setSelectedPlan(r.inboundPlanId)}
                                    >
                                        <td className="px-3 py-2 w-8" onClick={(e) => e.stopPropagation()}>
                                            <input
                                                type="checkbox"
                                                checked={selectedIds.has(r.inboundPlanId)}
                                                onChange={() => toggleSelectOne(r.inboundPlanId)}
                                                disabled={!canVoid}
                                                title={canVoid ? 'Select for bulk cancel' : 'Not cancellable'}
                                            />
                                        </td>
                                        <td className="px-3 py-2 text-xs text-gray-700 whitespace-nowrap">{formatRelativeOrAbs(r.planCreatedAt)}</td>
                                        <td className="px-3 py-2 whitespace-nowrap"><PlanIdCell planId={r.inboundPlanId} /></td>
                                        <td className="px-3 py-2 text-xs text-gray-700">{r.name || <span className="text-gray-400">—</span>}</td>
                                        <td className="px-3 py-2">{statusBadge(status)}</td>
                                        <td className="px-3 py-2 text-xs text-right">{r.shipmentCount ?? '-'}</td>
                                        <td className="px-3 py-2 text-xs text-right">{r.totalUnits ?? '-'}</td>
                                        <td className="px-3 py-2"><DestinationChips destinations={r.destinations} /></td>
                                        <td className="px-3 py-2 text-xs text-right">{formatMoney(r.totalCost)}</td>
                                        <td className="px-3 py-2 text-xs text-gray-700">{r.shippingMode || '-'}</td>
                                        <td className="px-3 py-2 text-xs whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                                            <a
                                                href={`https://sellercentral.amazon.com/fba/inbound/plan/details?planId=${r.inboundPlanId}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-indigo-600 hover:text-indigo-800 mr-2"
                                            >
                                                Seller Central
                                            </a>
                                            {canVoid && (
                                                <button
                                                    onClick={async (e) => {
                                                        e.stopPropagation();
                                                        if (!window.confirm(`Cancel plan ${r.inboundPlanId}?`)) return;
                                                        try {
                                                            await ApiClient.post(`/api/shipment-planner/cancel/${r.inboundPlanId}`);
                                                            handleRefresh();
                                                        } catch (err) {
                                                            alert('Failed: ' + (err.response?.data?.error || err.message));
                                                        }
                                                    }}
                                                    className="text-red-600 hover:text-red-800"
                                                >
                                                    Void
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {selectedPlan && (
                <DetailModal
                    planId={selectedPlan}
                    onClose={() => setSelectedPlan(null)}
                    onAfterAction={handleRefresh}
                />
            )}
        </div>
    );
}
